import { ValidClientTableNameChars, ValidListNameChars } from './constants';
import { Timestamp } from 'firebase/firestore';
import { storage } from '@/firebase';
import store from '@/store';
import { validateQueryFilters, validateSearchType } from '@/store/queryValidators';
import { v4 as uuidv4 } from 'uuid';
import { getAuth } from 'firebase/auth';
import { getDownloadURL, ref } from 'firebase/storage';
import { failureToast } from '@/store/toastHelpers';
import { FilterPresets } from '@/store/filterPresets';
import { apicalOAuth } from '@/store/apicalOAuth';

export function validRoles(featureKey) {
    const permissions = [
        { access: 'click-collective', roles: ['admin', 'analyst'] },
        { access: 'client-intake', roles: ['admin', 'analyst'] },
        { access: 'database-selector', roles: ['admin', 'analyst'] },
        { access: 'apical', roles: ['admin', 'analyst'] },
    ];
    return permissions.find((f) => f.access === featureKey).roles;
}

export async function getUserRole(auth) {
    const resp = await post_request('get-firestore-user-doc', { path: `user/${auth.lastNotifiedUid}` });
    if (resp.status !== 200) {
        console.error('Error parsing user doc for user role.');
    } else {
        const data = await resp.json();
        const userData = data.doc.default;
        return 'role' in userData ? userData.role : '';
    }
}

export function isListNameValid(name) {
    return name.match(ValidListNameChars);
}

export function isValidClientTableName(name) {
    return name.match(ValidClientTableNameChars);
}

export function getClickCollectiveOptions(access) {
    return [
        { name: 'Exclude', value: 0, setDisabled: false },
        { name: 'Exclusive', value: 1, setDisabled: !access },
        { name: 'No Filter', value: 2, setDisabled: !access },
    ];
}

export function getHighRiskOvermergedFilterOptions() {
    return [
        { name: 'Exclude', value: 0 },
        { name: 'Include', value: 1 },
    ];
}

export function initializeCCFilter(role) {
    let access = validRoles('click-collective').includes(role);
    const filterOptions = getClickCollectiveOptions(access);
    const filterDefaultValue = { ...filterOptions.find((f) => f.value === 0) };
    return { options: filterOptions, value: filterDefaultValue };
}

export function returnColumns(firstEntry) {
    if (firstEntry != null) {
        let tempColumns = Object.keys(firstEntry);
        return tempColumns;
    }
}

export function validateBasketDictionary(basket) {
    var basketValidation = [];
    var basketKeys = Object.keys(basket);
    function validateBasketFieldValue(val) {
        if (Array.isArray(val)) {
            return val !== null && val.length > 0;
        }
        return val !== null && typeof val !== 'undefined';
    }
    basketKeys.forEach((k) => {
        if (k !== 'type') {
            basketValidation.push(validateBasketFieldValue(basket[k]['value']));
        }
    });
    return basketValidation.every(Boolean);
}

export function isBasket(fieldName) {
    return fieldName.includes('basket)');
}

export function isTotalDonationBasket(fieldName) {
    return fieldName.includes('Count of donations to (loop - special basket)');
}

export function isUniqueDonationBasket(fieldName) {
    return fieldName.includes('Count of distinct donations in (loop - special basket)');
}

export function isDemographicBasket(fieldName) {
    return fieldName.includes('Demographic (or - special basket)');
}

export function isBasketWithValue(fieldName) {
    return !isDemographicBasket(fieldName);
}

export function isBasketWithObjectType(fieldName) {
    return isBasket(fieldName) && (isDemographicBasket(fieldName) || isTotalDonationBasket(fieldName));
}

export function isBasketWithArrayType(basket) {
    return Array.isArray(basket);
}

export function invalidMsgSuffix(msg, fieldName) {
    return msg + ' for field: ' + fieldName + '.';
}

export function queryValidation(fields) {
    let errMsgs = [];

    for (let i of fields) {
        if (i['type'] === 'group') {
            errMsgs.push(...queryValidation(i['children'])[1]);
        } else if (i['type'] === 'rule') {
            if (i.field.length === 0) {
                errMsgs.push('Invalid empty field name.');
            }
            if (!i.operator.includes('NULL') && (isBasketWithValue(i.field) || !isBasket(i.field))) {
                if (i.value === null || typeof i.value === 'undefined') {
                    errMsgs.push(invalidMsgSuffix('Invalid empty value', i.field));
                }
            }
            if (isBasket(i.field)) {
                if (!i.basket || i.basket === null || typeof i.basket === 'undefined') {
                    errMsgs.push(invalidMsgSuffix('Invalid empty or undefined basket', i.field));
                    return [false, errMsgs];
                }
                if (isBasketWithObjectType(i.field)) {
                    let validBasketFields = [...Object.keys(i.basket)].filter((f) => f !== 'type');
                    if (validBasketFields.length === 0) {
                        errMsgs.push(invalidMsgSuffix('Special basket does not have minimum of 1 field', i.field));
                    } else {
                        if (!validateBasketDictionary(i.basket)) {
                            errMsgs.push(invalidMsgSuffix('Invalid empty basket field(s)', i.field));
                        }
                    }
                } else if (isBasketWithArrayType(i.basket) && i.basket.length === 0) {
                    errMsgs.push(invalidMsgSuffix('Invalid empty basket', i.field));
                } else if (isUniqueDonationBasket(i.field) && i.basket !== null && i.value > i.basket.length) {
                    errMsgs.push(
                        invalidMsgSuffix(
                            'Basket value needs to be less than or equal the number of candidates',
                            i.field
                        )
                    );
                }
            }
        }
    }
    return [errMsgs.length === 0, errMsgs];
}

export function addFlags(msgQueue, errMsgs) {
    var msgCount = msgQueue.length;
    errMsgs.forEach((msg) => {
        msgQueue.push({
            severity: 'error',
            content: msg,
            life: 5000,
            id: msgCount++,
        });
    });
}

export function downloadFile(url) {
    const storageRef = ref(storage, url);
    getDownloadURL(storageRef)
        .then((dl_url) => {
            window.open(dl_url, '_blank');
        })
        .catch((error) => {
            console.error('Error downloading file', error);
        });
}

export async function getStoredLists(collection, docId) {
    try {
        const resp = await post_request('get-stored-lists', { user_id: docId, collection: collection });
        const data = await resp.json();

        data.saved_queries = data.saved_queries.map((list) => ({
            ...list,
            timestamp: formatTimestamp(list.timestamp),
        }));

        data.saved_matchlists = data.saved_matchlists.map((list) => ({
            ...list,
            timestamp: formatTimestamp(list.timestamp),
        }));

        return data;
    } catch (error) {
        console.error('Error fetching items:', error);
        return [];
    }
}

export function showFailedValidationMsg(specificMsg, toastObj) {
    toastObj.add({
        severity: 'error',
        summary: 'Reload Failed',
        detail: 'Cannot reload query due to: ' + specificMsg,
        life: 20000,
    });
}

export function identifyMasterListEntry(input) {
    return input.type === 'sent_list' && input.name === input.value.split('.').pop();
}

export function reformatMasterListForReload(sentListPayload) {
    /**
     * Identifies entries in the sentListPayload that is a master list and reformats the 'value' field to only contain the client's filename_prefix.
     * Older queries (before 06/11/25) store the full path to the master list in the 'value' field, which is not compatible with the current system.
     */
    if (!sentListPayload) {
        return null;
    }
    const reformattedMasterList = sentListPayload.map((entry) => {
        if (identifyMasterListEntry(entry)) {
            return { ...entry, value: entry.value.split('.').pop() };
        }
        return entry;
    });
    return reformattedMasterList;
}

/**
 * The list limit to show for a search type, shared by the acquisition-list and
 * matchlist pages.
 *
 * Called with no savedLimit on search-type change, where resetting to the new
 * type's max is correct. Called with the restored value when rendering a saved
 * query, so a reload keeps the limit it was saved with rather than being
 * clobbered back up to the max. Anything unusable (absent, empty, non-numeric,
 * non-positive) falls back to the max, and a saved limit above the max is
 * clamped -- mirroring the backend, where the limit can only ever reduce output.
 */
export function resolveListLimit(searchType, savedLimit) {
    const max = searchType.maxListLimit;
    const parsed = Number(savedLimit);
    const usable = savedLimit !== undefined && savedLimit !== null && savedLimit !== '' && parsed > 0;
    return usable ? Math.min(parsed, max) : max;
}

/**
 * The list's actual output columns, for the Redacted Columns picker.
 *
 * Computed server-side rather than mirrored here: the contact column names vary
 * by list template (email/email1, mobile/cell/phone) and a frontend copy would
 * drift, as AdditionalColumnOptions already does. Also returns the server's
 * verdict on the candidate set, so the tiers stay server-side. No BigQuery.
 *
 * Returns null on failure -- NOT an empty result. Callers prune against the
 * options, and a valid list type always projects something, so treating a failed
 * fetch as "no columns exist" would wipe a restored redaction set and export the
 * columns it was meant to drop.
 */
export async function fetchOutputColumns(payload) {
    try {
        const response = await post_request('fetch-output-columns', payload);
        if (response.status !== 200) {
            console.log(`Could not fetch output columns: ${response.status}.`);
            return null;
        }
        const body = await response.json();
        return {
            columns: body.message ?? [],
            redactionError: body.redactionError ?? null,
        };
    } catch (error) {
        console.log(error);
        return null;
    }
}

export function reloadQueryFilters(list, toastObj) {
    if (!store.getters.isAuthenticated) {
        this.showFailedValidationMsg('User Not Authenticated.');
    } else {
        this.queryFilters = { ...list.queryFilters };
        let allValid = validateQueryFilters(this.queryFilters.children, showFailedValidationMsg, toastObj);
        let searchTypeValid = validateSearchType(list.searchType.value);
        if (allValid && searchTypeValid) {
            this.$toast.add({
                severity: 'success',
                summary: 'Query Filters Validated',
                detail: '',
                life: 10000,
            });
            const params = {
                savedQuery: JSON.stringify(this.queryFilters),
                listType: JSON.stringify(list.searchType),
                // Shared by both branches below, so one assignment restores the
                // saved list limit on the matchlist and acquisition-list paths.
                // Absent on queries saved before the limit was persisted; each
                // page falls back to its search-type max in that case.
                savedListLimit: list.listLimit ?? '',
                // Absent before this feature; parsed to [] so a pre-feature
                // query reloads with nothing redacted rather than failing.
                savedRedactedColumns: JSON.stringify(list.redactedColumns ?? []),
            };

            const reformattedMasterList = reformatMasterListForReload(list.sentLists);
            params.suppressionFiles = JSON.stringify(reformattedMasterList);

            if (list.isMatchlist) {
                this.$router.push({
                    path: '/match-list',
                    name: 'Match List',
                    params: {
                        ...params,
                        loadWithMatchlist: list.sourceMatchList,
                        matchedOn: JSON.stringify(list.matchedOn),
                    },
                });
            } else {
                this.$router.push({
                    path: '/list-generation',
                    name: 'List Generation',
                    params: params,
                });
            }
        }
    }
}

export function formatDate(value) {
    let date = new Date(value * 1000);
    return date.toLocaleString('en-US', {});
}

// Generous default so a legitimate long backend call (e.g. a BQ load, capped by
// Cloud Run at ~300s) can finish, while a truly stalled request still rejects
// instead of hanging the UI forever.
const DEFAULT_REQUEST_TIMEOUT_MS = 330000;

export async function send_request(url, method, body, extraHeaders = {}, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS) {
    const auth = getAuth();
    const accessToken = await store.getters.idToken;
    const apiKey = await store.getters.apiKey;
    // AbortSignal.timeout rejects the fetch with a TimeoutError when it fires;
    // guard for older runtimes that lack it.
    const signal =
        typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(timeoutMs) : undefined;
    let response = await fetch(url, {
        method: method,
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + accessToken + ', Basic ' + apiKey,
            From: auth.currentUser.email,
            'X-Request-ID': uuidv4(),
            ...extraHeaders,
        },
        body: body,
        signal: signal,
    });
    return response;
}

export async function post_request(url_suffix, data, extraHeaders = {}, timeoutMs) {
    return await send_request(
        process.env.VUE_APP_API_URL + url_suffix,
        'POST',
        JSON.stringify(data),
        extraHeaders,
        timeoutMs
    );
}

export function requireApicalSignedIn(isSignedIn, toast) {
    if (isSignedIn) return true;
    toast.add({
        severity: 'warn',
        summary: 'Sign in to Apical first',
        detail: 'Use the Apical widget in the top to sign in, then try again.',
        life: 5000,
    });
    return false;
}

export async function postWithApicalToken(endpoint, orgId, payload) {
    // Mint a per-org OAuth access token (cached; silently renewed) and send it
    // in a custom header, not the body. The backend reads it via
    // req.headers.get("X-Apical-Token") and forwards it as Bearer to Apical.
    const token = await apicalOAuth().getTokenForOrg(orgId);
    return await post_request(endpoint, payload, { 'X-Apical-Token': token });
}

export async function get_request(url_suffix) {
    return await send_request(process.env.VUE_APP_API_URL + url_suffix, 'GET');
}

export async function getWithApicalToken(endpoint) {
    // The org-list endpoint needs a user-level OAuth token (no org chosen yet)
    // to identify the Clerk user; org-scoped tokens come later via
    // postWithApicalToken once an org is selected.
    const user = await apicalOAuth().getUser();
    return await send_request(process.env.VUE_APP_API_URL + endpoint, 'GET', undefined, {
        'X-Apical-Token': user?.access_token || '',
    });
}

export async function addToClientListData(clientID, fileName, fileID, fileType) {
    if (!clientID || !fileName || !fileID || !fileType) {
        throw new Error('Missing required parameters to add to client list data.');
    }
    const data = {
        client_id: clientID,
        file_name: fileName,
        file_id: fileID,
        file_type: fileType,
    };
    return await post_request('update-client-lists', data);
}

export async function removeFromClientListData(list, clientID) {
    const data = {
        client_id: clientID,
        file_name: list.name,
        file_id: list.id,
    };
    return await post_request('remove-client-sent-list', data);
}

export function listIsSyncingStatus(listData) {
    return 'syncing' in listData && listData['syncing'] === true;
}

export function markSyncingList(collectionList, list) {
    let storedLists = collectionList;

    let upd_obj = storedLists.findIndex((listRef) => listRef.id == list.id);
    storedLists[upd_obj].syncing = true;
    return storedLists;
}

export function unmarkSyncingList(collectionList, list) {
    let storedLists = collectionList;

    let upd_obj = storedLists.findIndex((listRef) => listRef.id == list.id);
    if (storedLists[upd_obj].syncing) {
        delete storedLists[upd_obj].syncing;
    }
    return storedLists;
}

export function updateStoredLists(collectionList, list) {
    let storedLists = collectionList;

    let upd_obj = storedLists.findIndex((listRef) => listRef.id == list.id);
    storedLists[upd_obj].listName = list.name;
    storedLists[upd_obj].isSynced = true;
    storedLists[upd_obj].sentTimestamp = list.sentTimestamp;

    return storedLists;
}

export function updateStoredListsOnReverseSync(collectionList, list) {
    let storedLists = collectionList;
    let upd_obj = storedLists.findIndex((ref) => ref.id == list.id);
    storedLists[upd_obj].isSynced = false;
    storedLists[upd_obj].sentTimestamp = null;
    storedLists[upd_obj].syncedFileName = null;

    return storedLists;
}

export async function postList(list) {
    const auth = getAuth();

    let data = {
        user_email: auth.currentUser.email,
        list: list,
    };

    try {
        const syncResponse = await post_request('sync-list-to-client', data);
        if (syncResponse.status !== 200) {
            const syncErrorMsg = await syncResponse.text();
            console.error('Error initiating list sync:', syncErrorMsg);
            return { message: syncErrorMsg, status: syncResponse.status };
        }

        const updateFirestoreOnSyncResp = await addToClientListData(list.clientId, list.name, list.id, 'sync');
        if (updateFirestoreOnSyncResp.status !== 200) {
            const updateErrorMsg = await updateFirestoreOnSyncResp.text();
            console.error('Error updating client docs on sync:', updateErrorMsg);
            return { message: updateErrorMsg, status: updateFirestoreOnSyncResp.status };
        }
        const syncResponseBody = await syncResponse.json();
        return { ...syncResponseBody, status: 200 };
    } catch (error) {
        console.error('Error at postList: ', error.message);
        return { message: 'Unable to initiate list sync: ' + error.message, status: 500 };
    }
}

export async function syncListAndUpdateDoc(listData, toastObj, storeObj) {
    toastObj.add({
        severity: 'info',
        summary: 'Syncing',
        detail: "The file '" + listData.name + "' is syncing. It may take a few seconds.",
        life: 3000,
    });

    if (listData.clientId === listData.selectedSentList.name) {
        let fsResponse = await post_request('get-firestore-doc', { path: `client/${listData.clientId}` });

        if (fsResponse.status !== 200) {
            failureToast(toastObj, 'Cannot Sync to a non-existent Client');
            return null;
        }

        const fsResponseBody = await fsResponse.json();
        const clientDoc = fsResponseBody['doc'];
        if ('filename_prefix' in clientDoc && clientDoc['filename_prefix']) {
            listData.selectedSentList.name = clientDoc['filename_prefix'];
        } else {
            failureToast(toastObj, 'Client attached to list is missing a master list');
            return null;
        }
    }
    const syncParsedResponse = await postList(listData);
    if (syncParsedResponse.status !== 200) {
        console.error('Unable to complete sync to client: ', syncParsedResponse.message);
        toastObj.remove();
        toastObj.add({
            severity: 'error',
            summary: 'An Error has Occurred',
            detail:
                'Unable to complete sync for list: ' + listData.id + '. Please contact the dev team for assistance.',
        });
        return { message: syncParsedResponse.message, status: syncParsedResponse.status };
    }

    const sentFSTimestamp = formatTimestamp(syncParsedResponse.sent_timestamp);

    try {
        storeObj.dispatch('updateDoc', {
            path: `saved_queries/${listData.id}`,
            payload: {
                listName: listData.name,
                syncedFileName: listData.selectedSentList.name,
                isSynced: true,
                sentTimestamp: sentFSTimestamp,
            },
        });
    } catch (error) {
        console.error(error);
        toastObj.remove();
        toastObj.add({
            severity: 'error',
            summary: 'An Error Has Occurred',
            detail: 'List synced successfully to client, but unable to update list docs to reflect this. Flag the dev team for assistance.',
            life: 50000,
        });
        return { message: error, status: 500 };
    }

    toastObj.remove();
    toastObj.add({
        severity: 'success',
        summary: 'Successful',
        detail: `List has been synced to ${listData.clientName}`,
        life: 5000,
    });

    return { message: sentFSTimestamp, status: 200 };
}

export function emptyRevert() {
    return {
        id: null,
        name: null,
        client: { name: null, id: null },
        sentTimestamp: null,
        isMatchlist: null,
    };
}

export function allowedRevertSync(data) {
    return 'sentTimestamp' in data && data['sentTimestamp'];
}

export async function revertListSync(list) {
    const auth = getAuth();
    const clientUID = list['client']['id'];

    let data = {
        user_email: auth.currentUser.email,
        list_id: list['id'],
        sent_timestamp: list['sentTimestamp'],
        client_id: clientUID,
    };

    try {
        const revertResponse = await post_request('revert-list-sync', data);
        if (revertResponse.status !== 200) {
            const revertMsg = await revertResponse.text();
            console.error('Unable to initiate revert list sync: ', revertMsg);
            return { message: revertMsg, status: revertResponse.status };
        }

        const updateFirestoreOnRevertResp = await removeFromClientListData(list, clientUID);
        if (updateFirestoreOnRevertResp.status !== 200) {
            const updateErrorMsg = await updateFirestoreOnRevertResp.text();
            console.error('Error updating client docs on revert sync:', updateErrorMsg);
            return { message: updateErrorMsg, status: updateFirestoreOnRevertResp.status };
        }

        return { message: 'List sync successfully reversed.', status: 200 };
    } catch (error) {
        return { message: 'Unable to revert list sync: ' + error.message, status: 500 };
    }
}

export async function onRevertListSyncConfirm(listData, toastObj, storeObj) {
    toastObj.add({
        severity: 'info',
        summary: 'Reversing Sync',
        detail: "File: '" + listData.name + "'.\nThis process may take a few seconds.",
        life: 3000,
    });

    let response = await revertListSync(listData);
    if (response.status !== 200) {
        toastObj.remove();
        toastObj.add({
            severity: 'error',
            summary: 'Sync Reversal Failed',
            detail: response.message,
            life: 10000,
        });
        return { message: response.message, status: response.status };
    }

    try {
        storeObj.dispatch('updateDoc', {
            path: `saved_queries/${listData.id}`,
            payload: {
                isSynced: false,
                syncedFileName: null,
                sentTimestamp: null,
            },
        });
    } catch (error) {
        console.error('Error updating list document after sync reversal:', error);
        toastObj.remove();
        toastObj.add({
            severity: 'error',
            summary: 'An Error Has Occurred',
            detail: 'List sync reversal successful, but unable to update list docs to reflect this. Check console logs for details.',
            life: 10000,
        });
        return { message: error, status: 500 };
    }

    toastObj.remove();
    toastObj.add({
        severity: 'success',
        summary: 'Successful',
        detail: `${response.message} Refresh the page to load changes.`,
        life: 5000,
    });

    return { message: '', status: 200 };
}

export async function getUploadedFiles(clientUUID) {
    const resp = await post_request('get-firestore-collection', { path: `client/${clientUUID}/uploads` });
    // Without this a 500 surfaced as `data.collection.map is not a function` (or a
    // JSON SyntaxError on an HTML body), which told callers nothing.
    if (resp.status !== 200) {
        throw new Error(`Failed to fetch uploads for client ${clientUUID} (status ${resp.status})`);
    }
    const data = await resp.json();

    data.collection = await data.collection.map((upload) => ({
        ...upload,
        timestamp: formatTimestamp(upload.timestamp),
    }));

    return data.collection.sort((a, b) => b.timestamp - a.timestamp);
}

export async function copyText(clipboardText, toastMessage = 'The text has been copied to your clipboard.') {
    await navigator.clipboard.writeText(clipboardText);
    if (toastMessage) {
        this.$toast.add({
            severity: 'success',
            summary: 'Copied',
            detail: toastMessage,
            life: 5000,
        });
    }
}

export function updateQueryDefaults(query, searchType, queryDefaultType, districtValues = []) {
    query.conj = searchType.conj;
    query.children =
        searchType.value === 8
            ? [...districtValues]
            : [...FilterPresets.find((f) => f.value === searchType.value)[queryDefaultType]];
}

export function formatDocWithTimestamp(doc) {
    Object.keys(doc).forEach((key) => {
        if (key.toLowerCase().includes('timestamp')) {
            doc[key] = formatTimestamp(doc[key]);
        }
    });

    return doc;
}

export function formatTimestamp(timestamp) {
    return Timestamp.fromMillis(Date.parse(timestamp));
}

export function loadMatchlist(fileName, router, toast) {
    if (fileName) {
        router.push({
            path: '/match-list',
            name: 'Match List',
            params: {
                loadWithMatchlist: fileName,
            },
        });
    } else {
        failureToast(toast, `Failure loading in with Matchlist (${fileName})`);
    }
}
export function loadSuppressionLists(suppressionFiles, router, toast) {
    if (suppressionFiles) {
        const formattedFiles = suppressionFiles.map((file) => {
            const fileType = file.type.includes('sent') ? 'sent_list' : file.type;
            return {
                name: file.name,
                type: fileType,
                value: file.value,
            };
        });

        router.push({
            path: '/list-generation',
            name: 'List Generation',
            params: {
                suppressionFiles: JSON.stringify(formattedFiles),
            },
        });
    } else {
        failureToast(toast, `Failure loading in with Suppression Lists (${suppressionFiles})`);
    }
}

export function replaceLinkNewShared(linkElement, hrefOrImgPath, isImg, callback) {
    const id = linkElement.getAttribute('id');
    const cloneLinkElement = linkElement.cloneNode(true);

    if (isImg) {
        cloneLinkElement.setAttribute('img', hrefOrImgPath);
        cloneLinkElement.setAttribute('src', hrefOrImgPath);
    } else {
        cloneLinkElement.setAttribute('href', hrefOrImgPath);
    }
    cloneLinkElement.setAttribute('id', id + '-clone');

    linkElement.parentNode.insertBefore(cloneLinkElement, linkElement.nextSibling);

    cloneLinkElement.addEventListener('load', () => {
        linkElement.remove();
        cloneLinkElement.setAttribute('id', id);

        if (callback) {
            callback();
        }
    });
}

export function replaceHtmlElementShared(elementNameA, elementNameB, pathA, pathB, theme, fileType, isImg, footer) {
    const linkA = document.getElementById(elementNameA);
    const hrefOrImgA = pathA + theme + fileType;
    const linkB = document.getElementById(elementNameB);
    const hrefOrImgB = pathB + theme + fileType;
    replaceLinkNewShared(linkA, hrefOrImgA, isImg);
    if (!footer) {
        replaceLinkNewShared(linkB, hrefOrImgB, isImg);
    }
}

export function numFormatter(num) {
    const lookup = [
        { value: 1, symbol: '' },
        { value: 1e3, symbol: 'K' },
        { value: 1e6, symbol: 'M' },
    ];
    const item = lookup.findLast((item) => num >= item.value);
    const decimals = item && item.symbol ? Math.abs(Math.round(num / item.value).toString().length - 3) : 0;
    const factor = Math.pow(10, decimals);
    const roundedNum = Math.floor((num / item.value) * factor) / factor;

    return item ? roundedNum.toFixed(decimals).concat(item.symbol) : '0';
}

export function numWithCommas(num) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
